import type React from "react";
import { useRef } from "react";
import { Loader2 } from "lucide-react";
import {
  STORAGE_FILE_TYPE_CONFIGS,
  StorageFileType,
} from "@shared/schemas/storage-file.schemas";
import type { EventParticipantSelfView } from "@shared/schemas/event-participant-views.schemas";
import { Button } from "@planta-v2/pds-ui/button";
import { BackButton } from "@planta-v2/pds-ui/back-button";
import { Avatar, AvatarFallback } from "@planta-v2/pds-ui/avatar";
import { PlantaAvatarImage } from "@planta-v2/pds-ui/planta-avatar-image";
import { Skeleton } from "@planta-v2/pds-ui/skeleton";
import { StringUtils } from "@frontend/shared/presentation/utils/string.utils";
import { i18n } from "@frontend/i18n";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { resolveParticipantDisplayName } from "@shared/utils/participant-name.utils";
import { useEventThemeFromConfig } from "@/shared/presentation/hooks/useEventThemeFromConfig";
import { useProfileEditController } from "../controllers/useProfileEditController";

const AVATAR_ACCEPT =
  STORAGE_FILE_TYPE_CONFIGS[StorageFileType.AVATAR_IMAGE].allowedMimeTypes.join(
    ",",
  );

const LABEL_CSSCLASS =
  "text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground";

function resolveDisplayName(participant: EventParticipantSelfView): string {
  return resolveParticipantDisplayName({
    firstName: participant.firstName.value,
    lastName: participant.lastName.value,
    fullName: participant.fullName.value,
    nickName: participant.nickName.value,
  });
}

function PhotoBody({
  participant,
  isUploading,
  isRemoving,
  saveError,
  onUploadPhoto,
  onRemovePhoto,
}: {
  readonly participant: EventParticipantSelfView;
  readonly isUploading: boolean;
  readonly isRemoving: boolean;
  readonly saveError: Error | null;
  readonly onUploadPhoto: (file: File) => Promise<void>;
  readonly onRemovePhoto: () => Promise<void>;
}): React.JSX.Element {
  const chooseInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const displayName = resolveDisplayName(participant);
  const photoUrl = participant.photoUrl?.value ?? null;
  const hasPhoto = photoUrl !== null && photoUrl !== "";
  const isBusy = isUploading || isRemoving;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file !== undefined) {
      void onUploadPhoto(file);
    }
    e.target.value = "";
  };

  return (
    <div className="flex flex-col items-center px-4 pb-8 pt-6">
      <Avatar key={photoUrl ?? "no-photo"} className="size-28">
        {hasPhoto && !isRemoving ? (
          <PlantaAvatarImage src={photoUrl} alt={displayName} />
        ) : null}
        <AvatarFallback className="bg-muted-foreground/20 font-serif text-4xl font-normal text-foreground/75">
          {isBusy ? (
            <Loader2
              className="h-8 w-8 animate-spin text-muted-foreground"
              data-testid="photo-page-spinner"
              aria-label={
                isUploading
                  ? i18n.t("user.profile.form.uploading")
                  : i18n.t("user.profile.form.removing")
              }
            />
          ) : (
            StringUtils.getInitials(displayName)
          )}
        </AvatarFallback>
      </Avatar>
      <p className="mt-4 text-center text-xs leading-snug text-muted-foreground">
        {hasPhoto
          ? i18n.t("user.profile.photo.currentHint")
          : i18n.t("user.profile.photo.initialsHint")}
      </p>
      {saveError !== null && (
        <p className="mt-3 text-center text-sm text-destructive">
          {saveError.message !== ""
            ? saveError.message
            : i18n.t("user.profile.form.errorSaving")}
        </p>
      )}
      <input
        ref={chooseInputRef}
        type="file"
        accept={AVATAR_ACCEPT}
        className="hidden"
        onChange={handleFileChange}
        data-testid="photo-page-file-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept={AVATAR_ACCEPT}
        capture="user"
        className="hidden"
        onChange={handleFileChange}
        data-testid="photo-page-camera-input"
      />
      <Button
        type="button"
        size="lg"
        className="mt-6 w-full"
        disabled={isBusy}
        onClick={(): void => chooseInputRef.current?.click()}
        data-testid="photo-page-choose-button"
      >
        {hasPhoto
          ? i18n.t("user.profile.photo.chooseAnother")
          : i18n.t("user.profile.photo.choose")}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="mt-2 w-full"
        disabled={isBusy}
        onClick={(): void => cameraInputRef.current?.click()}
        data-testid="photo-page-camera-button"
      >
        {i18n.t("user.profile.photo.takeNow")}
      </Button>
      {hasPhoto && (
        <Button
          type="button"
          variant="ghost"
          className="mt-1 w-full"
          disabled={isBusy}
          onClick={(): void => {
            void onRemovePhoto();
          }}
          data-testid="photo-page-remove-button"
        >
          {i18n.t("user.profile.photo.remove")}
        </Button>
      )}
      <p className={`${LABEL_CSSCLASS} mt-6 w-full`}>
        {i18n.t("user.profile.photo.whyTitle")}
      </p>
      <p className="mt-1.5 w-full text-sm leading-snug text-foreground">
        {i18n.t("user.profile.photo.whyBody")}
      </p>
    </div>
  );
}

export function ProfilePhotoPage(): React.JSX.Element {
  const { state, actions, nav } = useProfileEditController();
  useEventThemeFromConfig(state.eventIdPublic);

  const eventName =
    state.event !== undefined
      ? LocalizedTextUtils.resolve(state.event.name, i18n.getLocale())
      : "";

  return (
    <div
      className="flex h-dvh flex-col bg-surface"
      data-testid="profile-photo-page"
    >
      <header className="flex shrink-0 items-start justify-between bg-surface p-4 shadow-xs">
        <BackButton
          onClick={nav.toProfileEdit}
          data-testid="profile-photo-back-button"
          label={i18n.t("user.profile.title")}
        />
      </header>
      <div className="flex items-start gap-3 bg-surface p-4 shadow-xs">
        <div className="min-w-0 flex-1">
          <h1 className="page-title">{i18n.t("user.profile.photo.title")}</h1>
          {eventName !== "" && (
            <p className="truncate text-pds-2xs text-muted-foreground">
              {eventName}
            </p>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {state.isLoading ? (
          <div className="flex flex-col items-center gap-4 px-4 pt-6">
            <Skeleton className="size-28 rounded-full" />
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
          </div>
        ) : (
          state.participant !== undefined && (
            <PhotoBody
              participant={state.participant}
              isUploading={state.isUploadingPhoto}
              isRemoving={state.isRemovingPhoto}
              saveError={state.saveError}
              onUploadPhoto={actions.onUploadPhoto}
              onRemovePhoto={actions.onRemovePhoto}
            />
          )
        )}
      </div>
    </div>
  );
}
